import React, { FC, ChangeEvent, useEffect, useRef, ReactNode } from 'react'
import { InputContainer, InputInner, SearchIcon, Text } from './styles'
import styled from 'styled-components'
import { Box } from '../UI/Box'

const searchIcon = require('../../styles/images/search-icon.svg')

interface Props {
  value?: string
  width?: number
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  type?: string
  pattern?: string
  noIcon?: boolean
  max?: string | number | undefined
  triggerMaxReset?: () => void
  cursor?: string
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  rightContent?: ReactNode
}

const TextInput: FC<Props> = ({
  value,
  onChange,
  placeholder,
  type = 'text',
  pattern,
  noIcon = false,
  max = undefined,
  triggerMaxReset,
  cursor,
  onKeyDown,
  rightContent,
  width,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (max && value && inputRef?.current) {
      if (value > max && triggerMaxReset) {
        triggerMaxReset()
        alert(`Your max allocation is ${max}!`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [max, value, inputRef])

  return (
    <InputContainer width={width}>
      {!noIcon && <SearchIcon src={searchIcon.default} />}
      <InputInner
        width={width}
        cursor={cursor}
        ref={inputRef}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        pattern={pattern}
        step={0.1}
        noIcon={noIcon}
        min={type === 'number' ? 0 : undefined}
        max={type === 'number' ? max : undefined}
      />
      {rightContent && <Text>{rightContent}</Text>}
    </InputContainer>
  )
}

export default TextInput


interface RoundedTextInputProps {
  value?: string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  icon?: ReactNode
  disabled?: boolean
  hasFullWidth?: boolean
}
export const RoundedTextInput: FC<RoundedTextInputProps> = ({
  value,
  onChange,
  placeholder,
  icon,
  disabled,
  hasFullWidth = false,
}) => {
  return (
    <RoundedInputContainer hasFullWidth={hasFullWidth} g={8}>
      {!!icon && <IconWrapper>{icon}</IconWrapper>}
      <RoundedInput placeholder={placeholder} value={value} onChange={onChange} disabled={disabled} />
    </RoundedInputContainer>
  )
}

const RoundedInputContainer = styled(Box)<{ hasFullWidth?: boolean }>`
  display: flex;
  align-items: center;
  height: 62px;
  max-width: ${({ hasFullWidth }) => (hasFullWidth ? '100%' : '600px')};
  padding: 8px 8px 8px 20px;
  border-radius: 48px;
  border: 1px solid var(--stroke-secondary, #6c757d); /* Replace with your secondary stroke color */
  background-color: var(--surface-primary, #000); /* Replace with your primary surface color */
  box-sizing: border-box;

  :hover {
    border: 1px solid var(--stroke-secondary, #333);
    background: linear-gradient(0deg, var(--states-minimal-alpha-4, rgba(255, 255, 255, 0.04)) 0%, var(--states-minimal-alpha-4, rgba(255, 255, 255, 0.04)) 100%), var(--surface-invert-alpha-tertiary, rgba(0, 0, 0, 0.75));
  }
  
  :focus-within {  
    border: 1px solid var(--stroke-primary, #FFF);
    background: linear-gradient(0deg, var(--states-minimal-alpha-8, rgba(255, 255, 255, 0.08)) 0%, var(--states-minimal-alpha-8, rgba(255, 255, 255, 0.08)) 100%), var(--surface-invert-alpha-tertiary, rgba(0, 0, 0, 0.75));
  }
`;

// Styled input
const RoundedInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  color: #fff; /* Placeholder and text color */
  font-size: 16px;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6); /* Placeholder color */
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Styled search button
const SearchButton = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border: none;
  border-radius: 50%;
  background-color: var(--surface-brand, #FF0);
  cursor: pointer;
`;

// const SearchIcon = styled.svg`
//   width: 16px;
//   height: 16px;
//   fill: #000; /* Icon color */
// `;
